import React, { useState } from 'react'
import { Head, Link, router, usePage } from '@inertiajs/react'
import {
  Container, Typography, Box, Button, Paper, Chip,
  TextField, MenuItem, Select, FormControl, InputLabel, Stack,
  Autocomplete,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import LocationsMap from '../../components/LocationsMap'
import LocationCard from '../../components/LocationCard'
import SectionHeading from '../../components/SectionHeading'

interface GroupOption {
  id: number
  name: string
  company: string
}

interface GroupRef {
  id: number
  name: string
}

interface Location {
  id: number
  slug: string
  name: string
  address: string | null
  city: string | null
  country: string | null
  category: string | null
  description: string | null
  latitude: number | null
  longitude: number | null
  google_place_id: string | null
  image_url: string | null
  groups: GroupRef[]
  created_at: string
}

interface SearchableLocation {
  id: number
  name: string
  address: string | null
}

interface Props {
  locations: Location[]
  categories: string[]
  groups: GroupOption[]
  all_locations: SearchableLocation[]
  filters: { category: string | null; q: string | null; group_ids: string[] | null; ids: number[] | null; sort: string; direction: string }
}

const categoryLabels: Record<string, string> = {
  restaurant: 'Restaurant',
  cafe: 'Cafe',
  filming_location: 'Filming Location',
  practice_studio: 'Practice Studio',
  entertainment_venue: 'Entertainment Venue',
  shop: 'Shop',
  landmark: 'Landmark',
  other: 'Other',
}

type SortableColumn = 'name' | 'address' | 'category' | 'groups' | 'created_at'
type SortDirection = 'asc' | 'desc'

const SORT_OPTIONS: { value: string; sort: SortableColumn; direction: SortDirection; label: string }[] = [
  { value: 'created_at:desc', sort: 'created_at', direction: 'desc', label: 'Newest first' },
  { value: 'created_at:asc', sort: 'created_at', direction: 'asc', label: 'Oldest first' },
  { value: 'name:asc', sort: 'name', direction: 'asc', label: 'Name (A→Z)' },
  { value: 'name:desc', sort: 'name', direction: 'desc', label: 'Name (Z→A)' },
  { value: 'address:asc', sort: 'address', direction: 'asc', label: 'Address (A→Z)' },
  { value: 'address:desc', sort: 'address', direction: 'desc', label: 'Address (Z→A)' },
  { value: 'category:asc', sort: 'category', direction: 'asc', label: 'Category (A→Z)' },
  { value: 'category:desc', sort: 'category', direction: 'desc', label: 'Category (Z→A)' },
  { value: 'groups:desc', sort: 'groups', direction: 'desc', label: 'Most groups' },
  { value: 'groups:asc', sort: 'groups', direction: 'asc', label: 'Fewest groups' },
]

export default function LocationsIndex({ locations, categories, groups, all_locations, filters }: Props) {
  const page = usePage()
  const sharedProps = page.props as { google_maps_api_key?: string }
  const googleMapsApiKey = sharedProps.google_maps_api_key || ''
  // The "open" query param controls which map InfoWindow ("latch") is opened.
  // This is a pure display concern, so it is read client-side from the current
  // Inertia page URL rather than plumbed through the Rails controller props.
  const openParam = new URLSearchParams(page.url.split('?')[1] || '').get('open')
  const initialOpenId = openParam ? Number(openParam) : null
  const [category, setCategory] = useState(filters.category || '')
  const selectedGroupIds = (filters.group_ids || []).map(Number)
  const [selectedGroups, setSelectedGroups] = useState<GroupOption[]>(
    groups.filter((g) => selectedGroupIds.includes(g.id))
  )
  const initialSelectedLocationIds = (filters.ids || []).map(Number)
  const [selectedLocations, setSelectedLocations] = useState<SearchableLocation[]>(
    all_locations.filter((l) => initialSelectedLocationIds.includes(l.id))
  )
  const sortCol = (filters.sort || 'created_at') as SortableColumn
  const sortDir = (filters.direction || 'desc') as SortDirection
  const sortValue = `${sortCol}:${sortDir}`

  const buildParams = (overrides: Record<string, unknown> = {}) => {
    const params: Record<string, unknown> = {
      category: category || undefined,
      group_ids: selectedGroups.map((g) => g.id),
      ids: selectedLocations.map((l) => l.id),
      sort: sortCol,
      direction: sortDir,
      ...overrides,
    }
    if (Array.isArray(params.group_ids) && (params.group_ids as number[]).length === 0) {
      delete params.group_ids
    }
    if (Array.isArray(params.ids) && (params.ids as number[]).length === 0) {
      delete params.ids
    }
    return params
  }

  const applyFilters = (overrides: Record<string, unknown> = {}) => {
    router.get('/locations', buildParams(overrides) as Record<string, string>, { preserveState: true })
  }

  const handleGroupChange = (_: unknown, value: GroupOption[]) => {
    setSelectedGroups(value)
    const params = buildParams({ group_ids: value.map((g) => g.id) })
    if ((params.group_ids as number[] | undefined)?.length === 0) delete params.group_ids
    router.get('/locations', params as Record<string, string>, { preserveState: true })
  }

  const handleLocationSelectionChange = (_: unknown, value: SearchableLocation[]) => {
    setSelectedLocations(value)
    const params = buildParams({ ids: value.map((l) => l.id) })
    if ((params.ids as number[] | undefined)?.length === 0) delete params.ids
    router.get('/locations', params as Record<string, string>, { preserveState: true })
  }

  return (
    <>
      <Head title="Locations" />
      <Container maxWidth="xl" sx={{ py: { xs: 3, md: 4 }, px: { xs: 2, md: 4 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h5">Locations</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {locations.length} {locations.length === 1 ? 'place' : 'places'} on the map
            </Typography>
          </Box>
          <Button component={Link} href="/locations/new" variant="contained" startIcon={<AddIcon />}>
            Add Location
          </Button>
        </Box>

        <Box sx={{ mb: 3 }}>
          <LocationsMap
            apiKey={googleMapsApiKey}
            locations={locations.map((l) => ({
              id: l.id,
              name: l.name,
              address: l.address,
              category: l.category,
              latitude: l.latitude,
              longitude: l.longitude,
              google_place_id: l.google_place_id,
            }))}
            refitKey={`${category}|${selectedGroups.map((g) => g.id).join(',')}|${selectedLocations.map((l) => l.id).join(',')}`}
            initialOpenId={initialOpenId}
          />
        </Box>

        <Paper
          sx={{
            p: { xs: 2, md: 2.5 },
            mb: 3,
            backgroundColor: '#fff',
            boxShadow: '0 2px 12px rgba(123, 91, 214,0.08)',
          }}
        >
          <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', gap: 2 }}>
            <Autocomplete
              multiple
              size="small"
              options={all_locations}
              getOptionLabel={(option) => option.name}
              value={selectedLocations}
              onChange={handleLocationSelectionChange}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search Locations"
                  placeholder="Type to search…"
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box>
                    <Typography variant="body2">{option.name}</Typography>
                    {option.address && (
                      <Typography variant="caption" color="text.secondary">{option.address}</Typography>
                    )}
                  </Box>
                </li>
              )}
              sx={{ minWidth: 260, flexGrow: 1 }}
            />
            <FormControl size="small" sx={{ minWidth: 170 }}>
              <InputLabel>Category</InputLabel>
              <Select
                value={category}
                label="Category"
                onChange={(e) => {
                  setCategory(e.target.value)
                  applyFilters({ category: e.target.value || undefined })
                }}
                sx={{ borderRadius: 999 }}
              >
                <MenuItem value="">All Categories</MenuItem>
                {categories.map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    {categoryLabels[cat] || cat}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Autocomplete
              multiple
              size="small"
              options={groups}
              getOptionLabel={(option) => option.name}
              value={selectedGroups}
              onChange={handleGroupChange}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              renderInput={(params) => (
                <TextField {...params} label="Filter by Groups" placeholder="Select groups..." />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box>
                    <Typography variant="body2">{option.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{option.company}</Typography>
                  </Box>
                </li>
              )}
              sx={{ minWidth: 280, flexGrow: 1 }}
            />
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Sort by</InputLabel>
              <Select
                value={sortValue}
                label="Sort by"
                onChange={(e) => {
                  const opt = SORT_OPTIONS.find((o) => o.value === e.target.value)
                  if (opt) applyFilters({ sort: opt.sort, direction: opt.direction })
                }}
                sx={{ borderRadius: 999 }}
              >
                {SORT_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          {selectedGroups.length > 0 && (
            <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}>
              {selectedGroups.map((g) => (
                <Chip
                  key={g.id}
                  label={g.name}
                  onDelete={() => handleGroupChange(null, selectedGroups.filter((x) => x.id !== g.id))}
                />
              ))}
            </Stack>
          )}
        </Paper>

        <SectionHeading
          title={selectedGroups.length > 0 ? `Filtered places (${locations.length})` : 'All places'}
        />

        {locations.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No locations found. Try clearing your filters or{' '}
              <Link href="/locations/new" style={{ color: '#7B5BD6', fontWeight: 600 }}>add a new location</Link>.
            </Typography>
          </Paper>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                lg: 'repeat(3, 1fr)',
              },
            }}
          >
            {locations.map((location) => (
              <LocationCard
                key={location.id}
                location={{ ...location, thumbnail_url: location.image_url }}
              />
            ))}
          </Box>
        )}
      </Container>
    </>
  )
}
